import { BRAND_AUDIENCES_FULL_TEXT, BRAND_IMPORT_ERROR_TEXT, BRAND_LIMITS } from '@mg/shared';

/**
 * Brand Setup's own words (web.md §2.4, §4.3, §5.1-§5.7; spec:38-157). The words it shares with the
 * Edit pages (headings, section titles, start choices, toasts, the members' refusal) are in
 * features/brand/brandCopy.ts. No string here says "this brand" or "this company" (wording.gate.test.ts).
 */

// ── Create Brand Workspace ──────────────────────────────────────────────────────

export const CREATE_COPY = {
  /** The page's sentence, kept from today's Create page (spec:38). */
  description:
    "A workspace keeps a brand's content, sources, and digests fully separate. You can switch between workspaces any time.",
  namePlaceholder: 'e.g. Northwind Orthopedics',
  websitePlaceholder: 'https://www.yourbrand.com',
  cancel: 'Cancel',
  submit: 'Create Brand Workspace',
  failed: 'Could not create the workspace. The API may be unreachable — try again.',
} as const;

// ── The wizard's frame ──────────────────────────────────────────────────────────

export const BAR_COPY = {
  back: 'Back',
  exit: 'Exit onboarding',
  /** The bar's Exit below `sm`; its accessible name stays "Exit onboarding". */
  exitShort: 'Exit',
  skip: 'Skip for now',
  next: 'Next',
  finish: 'Finish setup',
  reload: 'Reload',
} as const;

/** The Unsaved changes dialog's body when leaving a step (web.md §4.3). */
export const WIZARD_UNSAVED_MESSAGE =
  "This step has changes that haven't been saved. Save them before leaving, or discard them.";

/** A step save that failed for a reason the server did not put in words. */
export const STEP_SAVE_FAILED = "Couldn't save this step. Try again.";

/** Skip for now or Exit without saving could not record where Brand Setup reopens. */
export const POSITION_SAVE_FAILED = "Couldn't save where you left off. Try again.";

/** The profile could not be read again after a version conflict. */
export const RELOAD_FAILED = "Couldn't load the latest version of your brand's details. Try again.";

/** The wizard could not load the brand's details at all. */
export const LOAD_FAILED = "We couldn't load your brand's details. Check your connection and try again.";

export const EXIT_DIALOG_COPY = {
  title: 'Exit onboarding?',
  body: "Steps you've finished are saved. You can continue Brand Setup any time from Edit Brand Workspace.",
  saveAndExit: 'Save and exit',
  exitWithoutSaving: 'Exit without saving',
  stay: 'Continue',
  invalid: "Couldn't save this step. Fix the highlighted fields, or exit without saving.",
  saving: 'Saving this step…',
} as const;

/** "Step 3 of 8". */
export const stepOfText = (step: number, count: number) => `Step ${step} of ${count}`;

// ── Step 1: the document box ────────────────────────────────────────────────────

export const DOCUMENT_BOX_COPY = {
  heading: 'Upload Brand Guideline Document',
  help: 'One Word document (.docx) or PDF, up to 10 MB. Uploading another file replaces the current one.',
  inputLabel: 'Brand guideline document',
  choose: 'Choose a file',
  replace: 'Replace file',
  remove: 'Remove',
  current: (fileName: string, date: string) => `Current document: ${fileName}, uploaded ${date}`,
  uploading: (percent: number | null) => (percent === null ? 'Uploading…' : `Uploading… ${percent}%`),
  uploadFailed: "Couldn't upload your document. Try again.",
} as const;

/**
 * Revisit step 1 under Re-read my sources (the "Onboarding wizard (revisit)" board): the sources as rows
 * with how each was read, to change before Next re-reads them. A failed page's words are the spec's
 * (spec:366-368, WEB_PAGE_FAILURE_TEXT).
 */
export const REVISIT_SOURCES_COPY = {
  /** A re-read is running or being reviewed: its sources stay as they were until it is done. */
  held: 'A re-read of these sources is open. Undo it to re-read different sources.',
  /** The stored sources could not be read: the boxes stay locked, so nothing replaces what is stored. */
  loadFailed: "We couldn't load your sources. Reload the page to change them, or click Next to re-read them as they are.",
  /** Save and exit with source changes on revisit step 1: they are read by Next, never saved. */
  unsentOnExit:
    'Your document and web page changes are only used when AI re-reads them. Click Continue, then Next to re-read them, or Exit without saving to drop them.',
  documentHeading: 'Upload Brand Guideline Document',
  documentHelp: 'Upload one Word document or PDF of your brand guidelines.',
  uploadDocument: 'Upload document',
  replaceDocument: 'Replace document',
  removeDocument: (fileName: string) => `Remove ${fileName}`,
  documentRemoved: "Removed. It won't be read.",
  undoRemove: 'Undo',
  pagesHeading: 'Scan web pages for brand voice',
  pagesHelp: `Add pages from your brand's website or social profiles. Up to ${BRAND_LIMITS.urls} public pages.`,
  pagePlaceholder: 'https://www.yourbrand.com/about',
  pageInputLabel: 'Web page address',
  addPage: 'Add URL',
  removePage: (url: string) => `Remove ${url}`,
  /** A source added since the last read: Next reads it. */
  fresh: "New. It's read when you click Next.",
  tryAgain: 'Try again',
  /** A failed page the person asked to read again. */
  retrying: "We'll try this page again when you click Next.",
  added: (url: string) => `Added ${url}.`,
  /** Pasted lines that are not web addresses, left in the box to fix. */
  notAddresses: (n: number) =>
    n === 1 ? "1 line isn't a web address. It's in the address box to fix." : `${n} lines aren't web addresses. They're in the address box to fix.`,
} as const;

/** The checks before any upload (web.md §5.2); the server checks the bytes again. */
export const FILE_CHECK_TEXT = {
  type: BRAND_IMPORT_ERROR_TEXT.unsupported_file,
  size: 'That file is larger than 10 MB. Upload a smaller file.',
} as const;

// ── Steps 2-8: the import banner ───────────────────────────────────────────────

export const IMPORT_BANNER_COPY = {
  seeNotImported: "See what wasn't imported",
  chooseAnother: 'Choose another file',
  tryAgain: 'Try again',
  retryFailed: "Couldn't start reading your document again. Try again.",
  /** Release 4 (web pages), for a partial scan. */
  pagesFailed: (failed: number, total: number) => `We couldn't read ${failed} of ${total} web pages.`,
} as const;

export const NOT_IMPORTED_COPY = {
  title: "What wasn't imported",
  close: 'Close',
  /** The reason beside an audience the limit of 10 kept out (spec:337). */
  audienceLimit: `Limit of ${BRAND_LIMITS.audiences} audiences`,
} as const;

// ── Step 2: Copy from another workspace (spec:150-153) ─────────────────────────

export const COPY_FROM_COPY = {
  label: 'Copy from another workspace',
  placeholder: 'Choose a workspace',
  button: 'Copy',
  nothing: (workspace: string) => `${workspace} has no company voice and identity to copy.`,
  confirmTitle: 'Replace your company voice and identity?',
  confirmMessage: (workspace: string) => `Replace what's already in these fields with the text from ${workspace}?`,
  confirmLabel: 'Replace',
  cancelLabel: 'Cancel',
  copied: (workspace: string) =>
    `Copied from ${workspace}. Review the text and change anything that's different for your brand. This is a copy, so editing it doesn't change ${workspace}.`,
  undo: 'Undo',
  failed: "Couldn't copy from that workspace. Try again.",
} as const;

// ── Step 7: Audiences (spec:267) ───────────────────────────────────────────────

export const AUDIENCES_COPY = {
  add: 'Add audience',
  count: (n: number) => `${n} of ${BRAND_LIMITS.audiences} audiences`,
  full: `${BRAND_LIMITS.audiences} of ${BRAND_LIMITS.audiences} audiences. ${BRAND_AUDIENCES_FULL_TEXT}`,
} as const;

/** Step 6's label over the table: the Writing guidelines list (its column is headed "Guideline"). */
export const GUIDELINES_FIELD_LABEL = 'Writing guidelines';
